import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Loader2, Send, Clock, CheckCircle, AlertCircle, Bot, Building2, Settings } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    loadTickets();
  }, [statusFilter]);

  const loadTickets = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const url = statusFilter === "all" 
        ? `${API}/admin/tickets`
        : `${API}/admin/tickets?status=${statusFilter}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets);
    } catch (error) {
      toast.error("Error al cargar tickets");
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetails = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/admin/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data.ticket);
      setTicketMessages(response.data.messages);
    } catch (error) {
      toast.error("Error al cargar detalles");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(
        `${API}/admin/tickets/${selectedTicket.id}/respond`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Mensaje enviado");
      setNewMessage("");
      loadTicketDetails(selectedTicket);
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCloseTicket = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(
        `${API}/admin/tickets/${selectedTicket.id}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Ticket cerrado");
      setSelectedTicket(null);
      loadTickets();
    } catch (error) {
      toast.error("Error al cerrar ticket");
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { color: "bg-yellow-100 text-yellow-700", label: "Abierto", icon: Clock },
      in_progress: { color: "bg-blue-100 text-blue-700", label: "En progreso", icon: Loader2 },
      ai_resolved: { color: "bg-green-100 text-green-700", label: "IA Resolvió", icon: Bot },
      admin_required: { color: "bg-red-100 text-red-700", label: "Requiere Admin", icon: AlertCircle },
      waiting_user: { color: "bg-purple-100 text-purple-700", label: "Espera Usuario", icon: MessageSquare },
      closed: { color: "bg-slate-100 text-slate-700", label: "Cerrado", icon: CheckCircle }
    };
    const { color, label, icon: Icon } = config[status] || config.open;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: "bg-slate-100 text-slate-600",
      medium: "bg-blue-100 text-blue-600",
      high: "bg-orange-100 text-orange-600",
      urgent: "bg-red-100 text-red-600"
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${config[priority] || config.medium}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "-";
    }
  };

  // Count tickets by status
  const statusCounts = {
    all: tickets.length,
    admin_required: tickets.filter(t => t.status === "admin_required").length,
    open: tickets.filter(t => t.status === "open").length,
    ai_resolved: tickets.filter(t => t.status === "ai_resolved").length,
    closed: tickets.filter(t => t.status === "closed").length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-tickets-page">
      {/* Header */}
      <div>
        <h1 className="page-title">Monitor de Soporte</h1>
        <p className="page-subtitle">Gestiona los tickets de soporte técnico</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <button
          onClick={() => setStatusFilter("all")}
          className={`p-3 rounded-lg border text-center transition-colors ${
            statusFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:bg-slate-50"
          }`}
        >
          <p className="text-2xl font-bold">{statusCounts.all}</p>
          <p className="text-xs">Total</p>
        </button>
        <button
          onClick={() => setStatusFilter("admin_required")}
          className={`p-3 rounded-lg border text-center transition-colors ${
            statusFilter === "admin_required" ? "bg-red-600 text-white border-red-600" : "bg-white border-slate-200 hover:bg-red-50"
          }`}
        >
          <p className="text-2xl font-bold">{statusCounts.admin_required}</p>
          <p className="text-xs">Escalados</p>
        </button>
        <button
          onClick={() => setStatusFilter("open")}
          className={`p-3 rounded-lg border text-center transition-colors ${
            statusFilter === "open" ? "bg-yellow-500 text-white border-yellow-500" : "bg-white border-slate-200 hover:bg-yellow-50"
          }`}
        >
          <p className="text-2xl font-bold">{statusCounts.open}</p>
          <p className="text-xs">Abiertos</p>
        </button>
        <button
          onClick={() => setStatusFilter("ai_resolved")}
          className={`p-3 rounded-lg border text-center transition-colors ${
            statusFilter === "ai_resolved" ? "bg-green-600 text-white border-green-600" : "bg-white border-slate-200 hover:bg-green-50"
          }`}
        >
          <p className="text-2xl font-bold">{statusCounts.ai_resolved}</p>
          <p className="text-xs">IA Resolvió</p>
        </button>
        <button
          onClick={() => setStatusFilter("closed")}
          className={`p-3 rounded-lg border text-center transition-colors ${
            statusFilter === "closed" ? "bg-slate-600 text-white border-slate-600" : "bg-white border-slate-200 hover:bg-slate-50"
          }`}
        >
          <p className="text-2xl font-bold">{statusCounts.closed}</p>
          <p className="text-xs">Cerrados</p>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Tickets list */}
        <div className="lg:col-span-1">
          <Card className="cia-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Tickets</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {tickets.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">No hay tickets pendientes</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {tickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      onClick={() => loadTicketDetails(ticket)}
                      className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${
                        selectedTicket?.id === ticket.id ? "bg-blue-50" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-medium text-sm text-slate-900 line-clamp-1">{ticket.subject}</p>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-3 h-3 text-slate-400" />
                        <p className="text-xs text-slate-600">{ticket.user_email}</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500">{formatDate(ticket.created_at)}</p>
                        {getPriorityBadge(ticket.priority)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Ticket detail */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <Card className="cia-card h-full flex flex-col">
              <CardHeader className="pb-3 border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-base">{selectedTicket.subject}</CardTitle>
                      {getPriorityBadge(selectedTicket.priority)}
                    </div>
                    <p className="text-xs text-slate-500">{selectedTicket.user_email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(selectedTicket.status)}
                    {selectedTicket.status !== "closed" && (
                      <Button size="sm" variant="outline" onClick={handleCloseTicket}>
                        Cerrar
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs font-medium text-slate-500 mb-1">Descripción original:</p>
                  <p className="text-sm text-slate-700">{selectedTicket.description}</p>
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-0 flex flex-col">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[350px]">
                  {ticketMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === "user" ? "justify-start" : "justify-end"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.sender_type === "user"
                            ? "bg-slate-200 text-slate-900"
                            : msg.sender_type === "ai"
                            ? "bg-blue-100 text-blue-900"
                            : "bg-green-100 text-green-900"
                        }`}
                      >
                        <p className="text-xs font-medium mb-1 opacity-70">
                          {msg.sender_type === "user" ? "Usuario" : msg.sender_type === "ai" ? "IA" : "Admin"}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        <p className="text-xs opacity-50 mt-1">{formatDate(msg.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Admin reply */}
                {selectedTicket.status !== "closed" && (
                  <div className="p-4 border-t">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Escribe tu respuesta como administrador..."
                      rows={3}
                      className="mb-3"
                    />
                    <div className="flex justify-end">
                      <Button onClick={handleSendMessage} disabled={sendingMessage} className="cia-btn-primary">
                        {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                        Responder
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="cia-card h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Selecciona un ticket para ver los detalles</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTicketsPage;
